import { useEffect, useRef } from "react";
import { useBackClosable } from "@teachermate/shared/back";

/**
 * 느낌 예시 창 — 그림 만들기 「그림 느낌」(화면 1-B · D10)과 뮤직비디오 컷 만들기 「그림 분위기」가 함께 쓴다.
 * 모양은 「수업에서 나갈까요?」 창(`exit-dialog`)과 같다.
 * 「이 느낌으로 할게요」만 고른다 — 「다른 느낌 볼래요」·바깥 누르기·Esc 는 안 고르고 닫는다.
 */
export function StyleSampleDialog({
  kicker,
  label,
  desc,
  imageUrl,
  wide = false,
  onClose,
  onConfirm,
}: {
  kicker: string;
  label: string;
  desc: string;
  imageUrl: string;
  /** 16:9 예시(뮤직비디오 컷). 기본은 정사각(그림 만들기). */
  wide?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const trigger = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );
  // 창이 열린 채 뒤로가기 = 창만 닫힘(DESIGN-POLICY §9.H-18 v2.4 표 첫 줄)
  useBackClosable(true, onClose);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const returnTo = trigger.current;
    return () => {
      document.removeEventListener("keydown", onKey);
      returnTo?.focus({ preventScroll: true });
    };
  }, [onClose]);

  return (
    <div
      className="exit-dialog-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`exit-dialog pic-style-dialog${wide ? " wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pic-style-title"
        aria-describedby="pic-style-desc"
      >
        <button className="button-ghost exit-dialog-close" type="button" onClick={onClose}>
          <span aria-hidden="true">×</span>
          닫기
        </button>
        <p className="mv-kicker pic-modal-kicker">{kicker}</p>
        <h2 id="pic-style-title">{label}</h2>
        <p id="pic-style-desc">{desc}</p>
        <div className="pic-style-sample">
          <img
            src={imageUrl}
            alt={`${label} 느낌 예시 그림`}
            width={wide ? 640 : 560}
            height={wide ? 360 : 560}
          />
        </div>
        <div className="exit-dialog-actions pic-style-actions">
          <button className="button-secondary" type="button" onClick={onClose}>다른 느낌 볼래요</button>
          <button ref={confirmRef} className="button-primary" type="button" onClick={onConfirm}>이 느낌으로 할게요</button>
        </div>
      </div>
    </div>
  );
}
